'use strict';

import React, { Component } from 'react';
import { polyfill } from 'react-lifecycles-compat';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { DEFAULT_SCOPE } from './loading_bar_ducks';

export const UPDATE_TIME = 200;
export const MAX_PROGRESS = 100;
export const PROGRESS_INCREASE = 3;
export const ANIMATION_DURATION = UPDATE_TIME * 4;
export const TERMINATING_ANIMATION_DURATION = UPDATE_TIME / 2;

const initialState = {
  percent: 0,
  status: 'hidden',
};

export class LoadingBar extends Component {
  state = { ...initialState };

  componentDidMount() {
    if (this.state.status === 'starting') {
      this.start();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.status !== this.state.status) {
      if (this.state.status === 'starting') {
        this.start();
      }

      if (this.state.status === 'stopping') {
        this.stop();
      }
    }
  }

  componentWillUnmount() {
    clearInterval(this.progressIntervalId);
    clearTimeout(this.terminatingAnimationTimeoutId);
  }

  reset = () => {
    this.terminatingAnimationTimeoutId = null;
    this.setState(initialState);
  };

  newPercent = (percent, progressIncrease) => {
    const smoothedProgressIncrease =
      progressIncrease * Math.cos(percent * (Math.PI / 2 / 100));

    return percent + smoothedProgressIncrease;
  };

  simulateProgress = () => {
    this.setState((prevState, { maxProgress, progressIncrease }) => {
      let { percent } = prevState;

      const newPercent = this.newPercent(percent, progressIncrease);

      if (newPercent <= maxProgress) {
        percent = newPercent;
      }

      return { percent };
    });
  };

  start() {
    this.progressIntervalId = setInterval(
      this.simulateProgress,
      this.props.updateTime
    );

    this.setState({ status: 'running' });
  }

  stop() {
    clearInterval(this.progressIntervalId);
    this.progressIntervalId = null;

    const terminatingAnimationDuration =
      this.isShown() || this.props.showFastActions
        ? TERMINATING_ANIMATION_DURATION
        : 0;

    this.terminatingAnimationTimeoutId = setTimeout(
      this.reset,
      terminatingAnimationDuration
    );

    this.setState({ percent: 100 });
  }

  isShown() {
    return this.state.percent > 0 && this.state.percent <= 100;
  }

  buildStyle() {
    const animationDuration =
      this.state.status === 'stopping'
        ? TERMINATING_ANIMATION_DURATION
        : ANIMATION_DURATION;

    const style = {
      opacity: this.isShown() ? '1' : '0',
      transform: `scaleX(${this.state.percent / 100})`,
      transformOrigin: 'left',
      transition: `transform ${animationDuration}ms linear`,
      width: '100%',
      willChange: 'transform, opacity',
    };

    if (!this.props.className) {
      style.height = '3px';
      style.backgroundColor = 'red';
      style.position = 'absolute';
    }

    return {
      ...style,
      ...this.props.style,
    };
  }

  render() {
    if (this.state.status === 'hidden') {
      return <div />;
    }

    if (this.props.renderComponent) {
      return this.props.renderComponent(Math.round(this.state.percent));
    }

    return (
      <div className={this.props.className}>
        {Math.round(this.state.percent)}
      </div>
    );
  }

  static shouldStart(props, state) {
    return (
      props.loading > 0 &&
      ['hidden', 'stopping'].includes(state.status)
    );
  }

  static shouldStop(props, state) {
    return (
      props.loading === 0 &&
      ['starting', 'running'].includes(state.status)
    );
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (LoadingBar.shouldStart(nextProps, prevState)) {
      return { status: 'starting' };
    }

    if (LoadingBar.shouldStop(nextProps, prevState)) {
      return { status: 'stopping' };
    }

    return null;
  }
}

LoadingBar.propTypes = {
  className: PropTypes.string,
  loading: PropTypes.number,
  maxProgress: PropTypes.number,
  progressIncrease: PropTypes.number,
  showFastActions: PropTypes.bool,
  updateTime: PropTypes.number,
  scope: PropTypes.string,
  style: PropTypes.object,
  renderComponent: PropTypes.func,
};

LoadingBar.defaultProps = {
  className: '',
  loading: 0,
  maxProgress: MAX_PROGRESS,
  progressIncrease: PROGRESS_INCREASE,
  showFastActions: false,
  style: {},
  updateTime: UPDATE_TIME,
  scope: DEFAULT_SCOPE,
  renderComponent: null,
};

const mapStateToProps = (state, ownProps) => ({
  loading: state.loadingBar[ownProps.scope || DEFAULT_SCOPE],
});

polyfill(LoadingBar);

const ConnectedLoadingBar = connect(mapStateToProps)(LoadingBar);

export default ConnectedLoadingBar;
